import { useEffect, useState } from 'react';
import { Info, X } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * De informatiebanner bovenin de klantomgeving.
 *
 * De tekst komt van een beheerder (Beheer > Melding) en staat aan of uit; zolang
 * hij uitstaat rendert dit component niets en neemt het dus ook geen ruimte in.
 *
 * WAAROM DE BANNER ZELF OPHAALT
 *
 * Hij hangt niet aan usePracticeSession. Die hook haalt zijn gegevens één keer op
 * bij het openen van het scherm, en het opnamescherm staat een ochtend lang open --
 * een melding die om tien uur wordt aangezet zou dan tot de volgende herlaadactie
 * onzichtbaar blijven, en dat is precies het moment waarop je hem nodig hebt.
 *
 * WAAROM ELKE VIJF MINUTEN EN NIET VAKER
 *
 * Dit is een mededeling en geen alarm. Vijf minuten is snel genoeg om een
 * onderhoudsmelding op tijd op het scherm te krijgen, en traag genoeg dat een
 * scherm dat de hele dag openstaat er hooguit honderd keer om vraagt.
 *
 * WAAROM EEN FOUT NIETS DOET
 *
 * Zonder sessie geeft de route 401, en tijdens een storing kan hij helemaal
 * uitblijven. Geen van beide is iets waar de gebruiker een melding over hoort te
 * krijgen: hij is bezig met een consult, en de banner is bijzaak. Wat er dan
 * gebeurt is dat de vorige tekst blijft staan tot de volgende ronde.
 *
 * WAAROM JE HEM KUNT WEGKLIKKEN
 *
 * Gelezen is gelezen: daarna kost de banner alleen ruimte boven het gesprek, en in
 * een smal paneel van de host is die ruimte schaars. Weggeklikt blijft hij weg in
 * dit tabblad (sessionStorage), ook na de volgende ronde en na herladen. Zet de
 * beheerder een andere tekst, dan verschijnt die wel: het onthouden geldt alleen
 * voor de tekst die je wegklikte.
 */

/** Hoe vaak de banner opnieuw vraagt of er iets te melden is. */
const HERHAAL_MS = 5 * 60 * 1000;

/** Waar de tekst van de weggeklikte melding bewaard blijft, voor dit tabblad. */
const WEG_OPSLAG = 'vts.melding.weg';

function leesWeg(): string | null {
  try {
    return window.sessionStorage.getItem(WEG_OPSLAG);
  } catch {
    return null;
  }
}

function bewaarWeg(tekst: string): void {
  try {
    window.sessionStorage.setItem(WEG_OPSLAG, tekst);
  } catch {
    /* zonder opslag blijft hij weg tot het scherm opnieuw opent */
  }
}

export function InfoBanner({ actief = true }: { actief?: boolean }) {
  const [melding, setMelding] = useState<string | null>(null);
  const [weg, setWeg] = useState<string | null>(leesWeg);

  useEffect(() => {
    if (!actief) {
      // Geen sessie: ook geen oude tekst laten staan van een sessie die net is
      // weggevallen.
      setMelding(null);
      return;
    }

    let gestopt = false;

    const haalOp = async () => {
      try {
        const { notice } = await api.get<{ notice: string | null }>('/notice');
        if (!gestopt) setMelding(notice);
      } catch {
        /* zie de kop: een mislukte ronde laat staan wat er stond */
      }
    };

    void haalOp();
    const timer = setInterval(() => void haalOp(), HERHAAL_MS);

    return () => {
      gestopt = true;
      clearInterval(timer);
    };
  }, [actief]);

  if (!melding || melding === weg) return null;

  return (
    <div
      // role="status" en niet role="alert": een schermlezer leest dit voor wanneer
      // het hem uitkomt, in plaats van de gebruiker te onderbreken. Het is
      // informatie, geen fout.
      role="status"
      data-vts="melding"
      className="flex items-start gap-2 px-3 py-2 bg-info/10 border-b border-info/30 shrink-0"
    >
      <Info className="h-4 w-4 text-info shrink-0 mt-px" aria-hidden="true" />
      {/*
        whitespace-pre-line, zodat een beheerder er twee regels van kan maken zonder
        dat daar opmaak voor nodig is. De tekst gaat als tekst het scherm op en niet
        als HTML -- zie infra/sql/015_melding.sql.
      */}
      <p className="flex-1 min-w-0 text-xs leading-relaxed whitespace-pre-line">{melding}</p>
      <button
        type="button"
        data-vts="melding-sluiten"
        aria-label="Melding verbergen"
        title="Melding verbergen"
        className="shrink-0 -my-0.5 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => {
          bewaarWeg(melding);
          setWeg(melding);
        }}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
